import { uuidv7 } from "@ets/shared";
import { COOKIE_NAMES, TTL } from "@ets/types";
import type { CookieOptions } from "./cookie.js";
import { getCookie, setCookie } from "./cookie.js";

declare global {
  interface Window {
    _ets?: { pvid?: string };
  }
}

interface SpaGuard {
  id: string;
  path: string;
  search: string;
}

const SS_KEY = "ets_pageview";

const PVID_COOKIE_OPTIONS: CookieOptions = {
  maxAge: TTL.PAGEVIEW_SECONDS,
  sameSite: "Lax",
  secure: true,
};

/**
 * Resolve the pageview ID with the following priority:
 * 1. Pre-generated ID from Layer 0 (window._ets.pvid) — consume and delete
 * 2. SPA guard: sessionStorage entry matching current path + search
 * 3. Cookie ets_pvid
 * 4. Generate new UUID v7
 *
 * Persists to cookie (5min TTL) and sessionStorage (SPA guard).
 */
export function resolvePageviewId(): string {
  // 1. Layer 0 pre-generated ID
  const layer0Id = typeof window !== "undefined" ? window._ets?.pvid : undefined;
  if (layer0Id) {
    delete (window as Window)._ets?.pvid;
    persist(layer0Id);
    return layer0Id;
  }

  // 2. SPA guard — reuse if path + search match
  const guard = getSpaGuard();
  if (guard && guard.path === location.pathname && guard.search === location.search) {
    // Refresh cookie TTL
    setCookie(COOKIE_NAMES.PAGEVIEW_ID, guard.id, PVID_COOKIE_OPTIONS);
    return guard.id;
  }

  // 3. Cookie fallback
  const cookieId = getCookie(COOKIE_NAMES.PAGEVIEW_ID);
  if (cookieId) {
    setSpaGuard(cookieId);
    return cookieId;
  }

  // 4. Generate new UUID v7
  const newId = uuidv7();
  persist(newId);
  return newId;
}

function persist(id: string): void {
  setCookie(COOKIE_NAMES.PAGEVIEW_ID, id, PVID_COOKIE_OPTIONS);
  setSpaGuard(id);
}

function getSpaGuard(): SpaGuard | undefined {
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    if (!raw) return undefined;
    return JSON.parse(raw) as SpaGuard;
  } catch {
    return undefined;
  }
}

function setSpaGuard(id: string): void {
  try {
    const guard: SpaGuard = {
      id,
      path: location.pathname,
      search: location.search,
    };
    sessionStorage.setItem(SS_KEY, JSON.stringify(guard));
  } catch {
    // sessionStorage blocked or full
  }
}
